import { FOUNDED_SINCE, FOUNDED_YEAR } from '@/content/company-stats';

/** Section 15 — Footer. Tagline, accordion link sections, newsletter, legal. */

export const FOOTER_DESCRIPTION =
  'Toronto-based digital ecommerce agency. Web design, Shopify development, and SEO services for businesses across Canada.';

/**
 * Footer accordion-structured data (used by `Footer.tsx`).
 */
export const FOOTER_ACCORDIONS = [
  {
    title: 'Agency',
    categories: [
      {
        heading: 'Agency',
        links: [
          { label: 'About Us', href: '/about' },
          { label: 'Blog', href: '/blog' },
          // /glossary stays noindex,follow, but it had zero inbound links from
          // any non-glossary page (Ahrefs crawl, 2026-09-02), so its 68 terms —
          // each of which links back into a service page — were unreachable
          // for crawlers and visitors alike. A footer link gives the hub a
          // crawl path without putting it in the index.
          { label: 'Glossary', href: '/glossary' },
          // Free tool page of the pricing cluster (Oct 2026). The footer is its
          // sitewide crawl path; the pricing articles link it in context.
          { label: 'Shopify Fee Calculator', href: '/shopify-fee-calculator' },
          { label: 'Portfolio', href: '/portfolio' },
          // /book is in the sitemap at priority 0.8 yet was linked from a
          // single page; the shortest path from search to a booked call needs
          // a sitewide entry point.
          { label: 'Book a Call', href: '/book' },
          { label: 'Contact', href: '/contacts' },
        ],
      },
    ],
  },
  {
    title: 'Services',
    categories: [
      {
        heading: 'Services',
        links: [
          { label: 'All Services', href: '/shopify-services' },
          { label: 'Shopify Experts', href: '/shopify-experts' },
          // Sitewide crawl path into the migration cluster. Without a link from an established
          // page the eight articles are reachable only from the sitemap, which gets them indexed
          // but passes them almost no internal authority.
          // Named for the page it opens, not for the topic: the header
          // already carries a "Shopify Migration" link, to the different page
          // /shopify-experts/shopify-migration. Two identically-labelled links
          // to two live money pages appeared together on 65 of 67 pages, and
          // every one of them split the internal anchor text across both URLs.
          // The label is the page's own title and slug, so it disambiguates
          // without inventing a term.
          { label: 'Shopify Migration Services', href: '/shopify-migration-services' },
          { label: 'SEO', href: '/seo-services' },
          // Canada-wide hub: the only sitewide link into it, so the page has
          // an internal crawl path rather than being reachable via the
          // sitemap alone (finding conversion-r5l002).
          { label: 'Shopify Agency in Canada', href: '/shopify-agency' },
          // Plus cluster conversion page (Sep 2026 plan, C3); the footer is its
          // sitewide crawl path until the hubs link it contextually.
          { label: 'Shopify Plus Agency', href: '/shopify-plus-agency' },
          // Agency-facing partner page. Kept out of the merchant-facing main
          // nav; the footer is its sitewide crawl path.
          { label: 'White Label for Agencies', href: '/white-label-shopify-development' },
          // Accessibility cluster money page (feat/shopify-accessibility-pages);
          // the footer is its sitewide crawl path.
          { label: 'Shopify Accessibility Audit', href: '/shopify-accessibility-audit' },
        ],
      },
    ],
  },
] as const;

export const NEWSLETTER_CONTENT = {
  heading: 'Newsletter',
  /**
   * What subscribers actually get — the bare 'Newsletter' label gave no
   * reason to hand over an inbox slot (finding conversion-86lm1e).
   */
  valueProp:
    'One Shopify growth teardown per month — CRO, SEO, and launch lessons from client work.',
  placeholder: 'Your email',
  /** Accessible name for the email input — the visual design is
   *  placeholder-only, so screen readers need an explicit label. */
  emailAriaLabel: 'Email address for the newsletter',
  consentText:
    'I agree to the processing of my personal data in accordance with the',
  consentLinkText: 'Privacy Policy',
  consentLinkHref: '/legal/privacy',
  submitLabel: 'subscribe',
} as const;

export const FOOTER_FOUNDED_LABEL = `Shopify services ${FOUNDED_SINCE}`;

/**
 * The end of the range is the build year, not a literal.
 *
 * It was typed as `2026`, which is correct only until 1 January — after that
 * the footer of every page states a copyright that expired, on a site whose
 * whole pitch is that it keeps merchants' stores current. The start comes from
 * `FOUNDED_YEAR` so the two halves of the range can never drift apart
 * (finding presence-z384db).
 *
 * Evaluated once at module load, so on a statically rendered footer this is
 * the year the site was built. A build that sits unshipped across a New Year
 * shows the old year — the same as the literal did — but a deploy fixes it
 * instead of a human remembering to.
 */
const CURRENT_YEAR = new Date().getFullYear();

export const FOOTER_COPYRIGHT = `\u00A9 ${FOUNDED_YEAR}\u2013${Math.max(
  CURRENT_YEAR,
  FOUNDED_YEAR,
)} EVDEV. All rights reserved.`;

export const FOOTER_LEGAL_LINKS = [
  { label: 'Privacy Policy', href: '/legal/privacy' },
  { label: 'Terms of Service', href: '/legal/terms' },
  { label: 'Cookie Policy', href: '/legal/cookies' },
  { label: 'Accessibility', href: '/legal/accessibility' },
] as const;
